/**
 * SPIN TENET — Study & Scenario Redux State Slice (Gate 1 Foundation)
 *
 * Manages:
 * - Study metadata
 * - Scenarios and scenario switching
 * - Durable CFD setup (wind, domain, boundary conditions)
 * - Local storage persistence across page reloads
 *
 * G1 RULE:
 * Wind speed and direction are UNSET by default (null).
 * Zero implicit scientific default values (e.g., no hidden 5.0 m/s or 0°).
 * The user must explicitly define wind speed and direction.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  Study,
  Scenario,
  DomainSetup,
  BoundaryConditionsSetup
} from '@contracts/index';
import { DEFAULT_WIND_CONVENTION } from '@contracts/index';

const STORAGE_KEY = 'spin_tenet_study_state_v3';

export interface StudyState {
  study: Study;
}

const DEFAULT_SCENARIOS: Record<string, Scenario> = {
  'scenario-baseline': {
    id: 'scenario-baseline',
    name: 'Baseline',
    description: 'Initial site condition aerodynamic study.',
    isBaseline: true,
    cfdSetup: {
      wind: {
        speed: null,       // Explicitly unset; user must enter
        direction: null,   // Explicitly unset; user must enter
        convention: DEFAULT_WIND_CONVENTION
      },
      domain: {
        width: 100,
        length: 100,
        height: 30
      },
      boundaryConditions: {
        inlet: 'velocity_inlet',
        outlet: 'pressure_outlet',
        ground: 'no_slip_wall',
        buildings: 'no_slip_wall',
        sides: 'slip_wall',
        top: 'slip_wall'
      }
    }
  },
  'scenario-variant-a': {
    id: 'scenario-variant-a',
    name: 'Design Variant A',
    description: 'Alternative building envelope orientation study.',
    isBaseline: false,
    cfdSetup: {
      wind: {
        speed: null,       // Explicitly unset; user must enter
        direction: null,   // Explicitly unset; user must enter
        convention: DEFAULT_WIND_CONVENTION
      },
      domain: {
        width: 100,
        length: 100,
        height: 30
      },
      boundaryConditions: {
        inlet: 'velocity_inlet',
        outlet: 'pressure_outlet',
        ground: 'no_slip_wall',
        buildings: 'no_slip_wall',
        sides: 'slip_wall',
        top: 'slip_wall'
      }
    }
  }
};

const DEFAULT_STUDY: Study = {
  id: 'study-downtown-district',
  name: 'Downtown District',
  description: 'Urban microclimate and wind comfort investigation.',
  scenarios: DEFAULT_SCENARIOS,
  activeScenarioId: 'scenario-baseline'
};

function loadPersistedStudy(): Study {
  if (typeof window === 'undefined' || !window.localStorage) {
    return DEFAULT_STUDY;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STUDY;
    const parsed = JSON.parse(raw) as Study;
    if (parsed && parsed.scenarios && parsed.activeScenarioId && parsed.scenarios[parsed.activeScenarioId]) {
      return parsed;
    }
  } catch (err) {
    console.warn('[StudySlice] Error loading persisted study state:', err);
  }
  return DEFAULT_STUDY;
}

function persistStudy(study: Study): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(study));
  } catch (err) {
    console.warn('[StudySlice] Error persisting study state:', err);
  }
}

const initialState: StudyState = {
  study: loadPersistedStudy()
};

export const studySlice = createSlice({
  name: 'study',
  initialState,
  reducers: {
    setActiveScenario: (state, action: PayloadAction<string>) => {
      const scenarioId = action.payload;
      if (state.study.scenarios[scenarioId]) {
        state.study.activeScenarioId = scenarioId;
        persistStudy(state.study);
      }
    },
    updateWind: (
      state,
      action: PayloadAction<{ speed?: number | null; direction?: number | null }>
    ) => {
      const active = state.study.scenarios[state.study.activeScenarioId];
      if (!active) return;
      const { speed, direction } = action.payload;
      if (speed !== undefined) {
        if (speed === null) {
          active.cfdSetup.wind.speed = null;
        } else {
          const num = Number(speed);
          active.cfdSetup.wind.speed = Number.isFinite(num) ? Math.max(0, num) : null;
        }
      }
      if (direction !== undefined) {
        if (direction === null) {
          active.cfdSetup.wind.direction = null;
        } else {
          const num = Number(direction);
          active.cfdSetup.wind.direction = Number.isFinite(num)
            ? ((num % 360) + 360) % 360
            : null;
        }
      }
      persistStudy(state.study);
    },
    updateDomain: (state, action: PayloadAction<Partial<DomainSetup>>) => {
      const active = state.study.scenarios[state.study.activeScenarioId];
      if (!active) return;
      const { width, length, height } = action.payload;
      if (width !== undefined) {
        active.cfdSetup.domain.width = Math.max(1, Number(width) || 1);
      }
      if (length !== undefined) {
        active.cfdSetup.domain.length = Math.max(1, Number(length) || 1);
      }
      if (height !== undefined) {
        active.cfdSetup.domain.height = Math.max(1, Number(height) || 1);
      }
      persistStudy(state.study);
    },
    updateBoundaryConditions: (state, action: PayloadAction<Partial<BoundaryConditionsSetup>>) => {
      const active = state.study.scenarios[state.study.activeScenarioId];
      if (!active) return;
      Object.assign(active.cfdSetup.boundaryConditions, action.payload);
      persistStudy(state.study);
    },
    createScenario: (state, action: PayloadAction<{ id: string; name: string }>) => {
      const { id, name } = action.payload;
      const active = state.study.scenarios[state.study.activeScenarioId];

      // New scenario initializes with unset wind speed & direction (Test D requirement)
      state.study.scenarios[id] = {
        id,
        name,
        isBaseline: false,
        cfdSetup: {
          wind: {
            speed: null,       // Strictly unset
            direction: null,   // Strictly unset
            convention: DEFAULT_WIND_CONVENTION
          },
          domain: active
            ? { ...active.cfdSetup.domain }
            : { width: 100, length: 100, height: 30 },
          boundaryConditions: active
            ? { ...active.cfdSetup.boundaryConditions }
            : {
                inlet: 'velocity_inlet',
                outlet: 'pressure_outlet',
                ground: 'no_slip_wall',
                buildings: 'no_slip_wall',
                sides: 'slip_wall',
                top: 'slip_wall'
              }
        }
      };
      state.study.activeScenarioId = id;
      persistStudy(state.study);
    },
    resetStudyState: (state) => {
      state.study = JSON.parse(JSON.stringify(DEFAULT_STUDY));
      persistStudy(state.study);
    }
  }
});

export const {
  setActiveScenario,
  updateWind,
  updateDomain,
  updateBoundaryConditions,
  createScenario,
  resetStudyState
} = studySlice.actions;

export default studySlice.reducer;
